import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Pencil, Trash2, History, MessageSquareText, ArrowUpDown, Columns3, GripVertical, ShieldCheck, ShieldAlert, Download, Upload, Filter, Sparkles, Layers, ChevronLeft, ChevronRight, Phone, X as XIcon, Loader2, ArrowRight, Link as LinkIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadFromUrl, downloadText } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { AiDidCommand } from "@/components/AiDidCommand";
import { BulkAddDid } from "@/components/BulkAddDid";
import { ColumnsToolbar } from "@/components/ColumnsToolbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DidFeedStatus } from "@/pages/did/DidFeedStatus";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { useTablePagination, PageSizeSelect, TablePagination } from "@/components/TablePagination";
import { toast } from "sonner";

const APP_TONE = {
  "UCaaS": "bg-green-50 text-green-700 border-green-200",
  "Hospitality": "bg-blue-50 text-blue-700 border-blue-200",
  "HTTPS Fax": "bg-purple-50 text-purple-700 border-purple-200",
  "Portal Fax": "bg-violet-50 text-violet-700 border-violet-200",
  "Internet Fax": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  "UC Analog": "bg-yellow-50 text-yellow-700 border-yellow-200",
};
const STATUS_TONE = {
  "Active": "bg-green-50 text-green-700",
  "Pending": "bg-amber-50 text-amber-700",
  "Porting In": "bg-blue-50 text-blue-700",
  "Porting Out": "bg-indigo-50 text-indigo-700",
  "Suspended": "bg-orange-50 text-orange-700",
  "Disconnected": "bg-rose-50 text-rose-700",
};
const EMPTY = { number: "", application: "UCaaS", sms_enabled: false, first_enabled: "", treatment: "", carrier: "", carrier_status: "Active", rate_center: "", rate_center_state: "", cost: 0, partner_org_id: "", domain: "", sbc: "", e911_registered: false, billing: false, location: "" };
const fmtDate = (s) => (s ? new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

// Phone-keypad mapping so search matches BOTH raw digits and spelled-out vanity letters.
const KEYPAD = { A: "2", B: "2", C: "2", D: "3", E: "3", F: "3", G: "4", H: "4", I: "4", J: "5", K: "5", L: "5", M: "6", N: "6", O: "6", P: "7", Q: "7", R: "7", S: "7", T: "8", U: "8", V: "8", W: "9", X: "9", Y: "9", Z: "9" };
const vanityToDigits = (s) => (s || "").toUpperCase().split("").map((c) => (/[0-9]/.test(c) ? c : (KEYPAD[c] || ""))).join("");
const digitsOnly = (s) => (s || "").replace(/\D/g, "");
// Iter 82: display DIDs as +<digits> (no dashes/parens, no locale rules).
// We keep raw digits in the DB and only prefix "+" for staff readability.
const fmtDid = (s) => {
  const d = digitsOnly(s);
  return d ? `+${d}` : (s || "");
};

// Iter 88: DNC-status tone helper — small pill next to a number in the
// Number column. Kept ultra-compact so it doesn't blow up row height.
const DNC_TONE = {
  clean:          "bg-emerald-50 text-emerald-700 border-emerald-200",
  tcpa_litigator: "bg-rose-50    text-rose-700    border-rose-200",
  federal_dnc:    "bg-amber-50   text-amber-800   border-amber-200",
  invalid:        "bg-slate-100  text-slate-500   border-slate-200",
  unknown:        "bg-slate-50   text-slate-500   border-slate-200",
};
const DNC_LABEL = {
  clean:          "Clean",
  tcpa_litigator: "TCPA",
  federal_dnc:    "DNC",
  invalid:        "Invalid",
  unknown:        "?",
};
const LINE_TYPE_TONE = {
  Mobile:   "bg-sky-50    text-sky-700    border-sky-200",
  Landline: "bg-indigo-50 text-indigo-700 border-indigo-200",
  VoIP:     "bg-violet-50 text-violet-700 border-violet-200",
  Unknown:  "bg-slate-50  text-slate-500  border-slate-200",
};
const NumberCell = ({ d }) => (
  <div className="flex items-center gap-1.5">
    <span className="font-medium text-slate-800 font-mono">{fmtDid(d.number)}</span>
    {d._lookup && d._lookup.line_type && d._lookup.line_type !== "Unknown" && (
      <span title={`Line type: ${d._lookup.line_type}${d._lookup.network_name ? ` · ${d._lookup.network_name}` : ""}`}
            className={`rounded-full border px-1.5 py-0 text-[9px] font-semibold ${LINE_TYPE_TONE[d._lookup.line_type] || LINE_TYPE_TONE.Unknown}`}
            data-testid={`did-linetype-${d.did_id}`}>
        {d._lookup.line_type}
      </span>
    )}
    {/* Iter 88o: DID Feed marked this row as missing from the last upstream sync. */}
    {d.is_stale && (
      <span title={`Not seen in upstream feed since ${d.stale_since || "recently"}`}
            className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[9px] font-semibold text-amber-800"
            data-testid={`did-stale-${d.did_id}`}>
        Stale
      </span>
    )}
    {/* Iter 88f: DNC status is deliberately NOT shown on DID Master.
        DNC is a campaign-scoped billable compliance concern in AI Outbound,
        not a property of a UC-owned DID. See /outbound/lists/{lid}/dnc-scrub. */}
  </div>
);

// Column registry — order/visibility are user-customizable & persisted.
const COLUMNS = [
  { key: "number", label: "Number", sortable: true, text: (d) => fmtDid(d.number), render: (d) => <NumberCell d={d} /> },
  { key: "application", label: "Application", sortable: true, text: (d) => d.application || "", render: (d) => <Badge variant="outline" className={`text-[10px] ${APP_TONE[d.application] || ""}`}>{d.application || "—"}</Badge> },
  { key: "sms_enabled", label: "SMS", sortable: true, text: (d) => d.sms_enabled ? "On" : "Off", render: (d) => d.sms_enabled ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><MessageSquareText className="h-3.5 w-3.5" /> On</span> : <span className="text-xs text-slate-400">Off</span> },
  { key: "partner_name", label: "Partner", sortable: true, text: (d) => d.partner_name || "", render: (d) => <span className="text-slate-600">{d.partner_name || "—"}</span> },
  { key: "domain", label: "Domain", sortable: true, text: (d) => d.domain || "", render: (d) => <span className="text-slate-600">{d.domain || "—"}</span> },
  { key: "sbc", label: "SBC (Geo)", sortable: true, text: (d) => d.sbc || "", render: (d) => d.sbc ? <Badge variant="outline" className="border-slate-200 bg-slate-50 text-[10px] text-slate-600">{d.sbc}</Badge> : <span className="text-slate-400">—</span> },
  { key: "e911_registered", label: "911 Registered", sortable: true, text: (d) => d.e911_registered ? "Yes" : "No", render: (d) => d.e911_registered ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /> Yes</span> : <span className="inline-flex items-center gap-1 text-xs text-rose-500"><ShieldAlert className="h-3.5 w-3.5" /> No</span> },
  { key: "first_enabled", label: "First enabled", sortable: true, text: (d) => fmtDate(d.first_enabled), render: (d) => <span className="text-slate-600">{fmtDate(d.first_enabled)}</span> },
  { key: "last_updated", label: "Last updated", sortable: true, text: (d) => fmtDateTime(d.last_updated), render: (d) => <span className="text-slate-600">{fmtDateTime(d.last_updated)}</span> },
  { key: "treatment", label: "Treatment", sortable: true, text: (d) => d.treatment || "", render: (d) => <span className="text-slate-600">{d.treatment || "—"}</span> },
  { key: "carrier", label: "Carrier", sortable: true, text: (d) => d.carrier || "", render: (d) => <span className="text-slate-600">{d.carrier || "—"}</span> },
  { key: "carrier_status", label: "Carrier status", sortable: true, text: (d) => d.carrier_status || "", render: (d) => d.carrier_status ? <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[d.carrier_status] || ""}`}>{d.carrier_status}</Badge> : <span className="text-slate-400">—</span> },
  { key: "rate_center", label: "Rate center", sortable: true, text: (d) => d.rate_center || "", render: (d) => <span className="text-slate-600">{d.rate_center || "—"}</span> },
  { key: "rate_center_state", label: "Province/State", sortable: true, text: (d) => d.rate_center_state || "", render: (d) => <span className="text-slate-600">{d.rate_center_state || "—"}</span> },
  { key: "billing", label: "Billing", sortable: true, text: (d) => d.billing ? "Yes" : "No", render: (d) => d.billing ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /> Yes</span> : <span className="inline-flex items-center gap-1 text-xs text-rose-500"><ShieldAlert className="h-3.5 w-3.5" /> No</span> },
  { key: "cost", label: "Cost", sortable: true, align: "right", text: (d) => `$${Number(d.cost || 0).toFixed(2)}`, render: (d) => <span className="font-medium text-slate-700">${Number(d.cost || 0).toFixed(2)}</span> },
  { key: "carrier_cost", label: "Carrier cost", sortable: true, align: "right",
    text: (d) => (d.carrier_cost === "" || d.carrier_cost == null) ? "" : `$${Number(d.carrier_cost).toFixed(2)}`,
    render: (d) => (d.carrier_cost === "" || d.carrier_cost == null)
      ? <span className="text-slate-400">—</span>
      : <span className="font-medium text-slate-700">${Number(d.carrier_cost).toFixed(2)}</span> },
  { key: "active", label: "Active", sortable: true,
    text: (d) => d.active ? "Yes" : "No",
    render: (d) => d.active
      ? <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]" variant="outline">Active</Badge>
      : <Badge className="border-slate-200 bg-slate-50 text-slate-500 text-[10px]" variant="outline">Inactive</Badge> },
  { key: "territory", label: "Territory", sortable: true,
    text: (d) => d.territory || "",
    render: (d) => <span className="text-slate-600 font-mono text-xs">{d.territory || "—"}</span> },
  { key: "location", label: "Location", sortable: true,
    text: (d) => d.location || "",
    render: (d) => <span className="text-slate-600">{d.location || "—"}</span> },
  { key: "forward_to", label: "Forward to", sortable: true,
    text: (d) => d.forward_to || "",
    render: (d) => <span className="text-slate-600 font-mono text-xs">{d.forward_to || "—"}</span> },
  { key: "portal_status", label: "Portal", sortable: true,
    text: (d) => String(d.portal_status || ""),
    render: (d) => d.portal_status
      ? <Badge variant="outline" className="text-[10px]">{d.portal_status}</Badge>
      : <span className="text-slate-400">—</span> },
  { key: "last_upload", label: "Last upload", sortable: true,
    text: (d) => d.last_upload?.label || "",
    render: (d) => d.last_upload?.label
      ? <span className="text-xs text-slate-500" title={d.last_upload.label}>{d.last_upload.label}</span>
      : <span className="text-slate-400">—</span> },
];
const COL_MAP = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));
const ALL_KEYS = COLUMNS.map((c) => c.key);

export default function DidMaster() {
  const { user, has } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const PREF_KEY = `did_cols_v1_${user?.user_id || "anon"}`;
  const [rows, setRows] = useState([]);
  const [apps, setApps] = useState([]);
  const [sbcOptions, setSbcOptions] = useState({});
  const [partners, setPartners] = useState([]);
  const [carrierStatuses, setCarrierStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistedState("did.search", "");
  const [appFilter, setAppFilter] = usePersistedState("did.app", "all");
  const [sort, setSort] = usePersistedState("did.sort", { key: "last_updated", dir: "desc" });
  const [dialog, setDialog] = useState(null);
  const [logTarget, setLogTarget] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  // Iter 84: per-column filter dropdowns (server-side distinct).
  // Number is deliberately excluded — its values are unique so a
  // dropdown is meaningless there.
  const [colFilters, setColFilters] = usePersistedState("did.columns", {});
  const [colOptions, setColOptions] = useState({});
  const FILTERABLE_KEYS = new Set([
    "application", "carrier", "carrier_status", "partner_name",
    "domain", "sbc", "portal_status", "territory", "active",
    "sms_enabled", "e911_registered", "billing",
  ]);
  // Map column keys → backend field query param (mostly identical).
  const filterFieldFor = (k) => k;
  const loadColOptions = async (colKey) => {
    if (colOptions[colKey]) return;
    try {
      const { data } = await api.get("/dids/distinct", { params: { field: filterFieldFor(colKey) } });
      setColOptions((o) => ({ ...o, [colKey]: data.values || [] }));
    } catch { setColOptions((o) => ({ ...o, [colKey]: [] })); }
  };
  const [showFilters, setShowFilters] = useState(true);

  // Iter 82/83: CSV upload with import-diff preview + upload-history dialogs.
  const [uploadBusy,      setUploadBusy]      = useState(false);
  const [historyOpen,     setHistoryOpen]     = useState(false);
  const [historyItems,    setHistoryItems]    = useState([]);
  const [preview,         setPreview]         = useState(null); // {staging_id, diff, ...}
  const [deleteRemoved,   setDeleteRemoved]   = useState(false);
  const [conflictPicks,   setConflictPicks]   = useState({});
  const csvUploadRef = useRef(null);

  const openCsvUpload = () => csvUploadRef.current?.click();
  const onCsvChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be picked twice in a row
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) { toast.error("Please pick a .csv file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    const t = toast.loading("Parsing CSV…");
    try {
      setUploadBusy(true);
      const { data } = await api.post("/dids/upload-csv", fd);
      setPreview(data);
      setConflictPicks({});
      setDeleteRemoved(false);
      toast.success(`Parsed ${data.row_count.toLocaleString()} rows — review the diff below.`, { id: t });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Parse failed", { id: t });
    } finally { setUploadBusy(false); }
  };

  const applyImport = async ({ allCsv } = {}) => {
    if (!preview) return;
    const payload = {
      delete_removed: !!deleteRemoved,
      conflict_resolutions: conflictPicks,
      accept_all_conflicts_csv: !!allCsv,
    };
    const t = toast.loading("Applying import…");
    try {
      setUploadBusy(true);
      const { data } = await api.post(`/dids/imports/${preview.staging_id}/apply`, payload);
      const c = data.counts;
      toast.success(
        `+${c.added} · ~${c.updated_auto} auto · ${c.conflicts_csv} conflicts→CSV · ${c.conflicts_kept}→DB · -${c.removed}`,
        { id: t, duration: 6000 }
      );
      setPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Apply failed", { id: t });
    } finally { setUploadBusy(false); }
  };

  const cancelImport = async () => {
    if (!preview) return;
    try { await api.post(`/dids/imports/${preview.staging_id}/cancel`); } catch { /* fine */ }
    setPreview(null);
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get("/uploads/history", { params: { scope: "dids", limit: 25 } });
      setHistoryItems(data.items || []);
    } catch { setHistoryItems([]); }
  };

  // Column prefs (order + hidden), persisted per user.
  const [order, setOrder] = useState(ALL_KEYS);
  const [hidden, setHidden] = useState([]);
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem(PREF_KEY) || "{}");
      const ord = Array.isArray(p.order) ? p.order.filter((k) => COL_MAP[k]) : [];
      setOrder([...ord, ...ALL_KEYS.filter((k) => !ord.includes(k))]);
      setHidden(Array.isArray(p.hidden) ? p.hidden.filter((k) => COL_MAP[k]) : []);
    } catch { /* defaults */ }
  }, [PREF_KEY]);
  const persist = (ord, hid) => localStorage.setItem(PREF_KEY, JSON.stringify({ order: ord, hidden: hid }));
  const toggleHidden = (key) => setHidden((h) => { const nh = h.includes(key) ? h.filter((k) => k !== key) : [...h, key]; persist(order, nh); return nh; });
  const resetCols = () => { setOrder(ALL_KEYS); setHidden([]); persist(ALL_KEYS, []); };

  // Iter 90j — up/down reordering to match Partners' ColumnsToolbar contract.
  const moveCol = (key, dir) => {
    setOrder((cur) => {
      const idx = cur.indexOf(key);
      const tgt = idx + dir;
      if (idx < 0 || tgt < 0 || tgt >= cur.length) return cur;
      const next = [...cur];
      [next[idx], next[tgt]] = [next[tgt], next[idx]];
      persist(next, hidden);
      return next;
    });
  };

  // Shape adapter: ColumnsToolbar expects {id, label, always}. DidMaster
  // uses {key, label}. Pin the DID number column as always-visible.
  const toolbarColumns = useMemo(
    () => COLUMNS.map((c) => ({ id: c.key, label: c.label, always: c.key === "number" })),
    []
  );
  const toolbarVisibleIds = useMemo(
    () => order.filter((k) => !hidden.includes(k)),
    [order, hidden]
  );

  const visibleCols = useMemo(() => order.filter((k) => !hidden.includes(k)).map((k) => COL_MAP[k]).filter(Boolean), [order, hidden]);

  const canCreate = has("did.create"), canEdit = has("did.edit"), canDelete = has("did.delete");

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);          // zero-indexed
  const [pageSize, setPageSize] = useState(50);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  // Iter 84: bulk-select + reassign partner.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOrgId,   setBulkOrgId]   = useState("");
  const [bulkBusy,    setBulkBusy]    = useState(false);
  // Iter 87: "Select all N matching current filter" mode. When true, the
  // Reassign action ignores the per-row selection and hits every DID
  // matching the server-side filter set.
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  const toggleSelect = (did_id) => {
    setSelectAllMatching(false);
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(did_id)) n.delete(did_id); else n.add(did_id);
      return n;
    });
  };
  const allSelected = rows.length > 0 && rows.every((d) => selectedIds.has(d.did_id));
  const toggleSelectAll = () => {
    setSelectAllMatching(false);
    setSelectedIds((s) => {
      if (allSelected) {
        const n = new Set(s);
        rows.forEach((d) => n.delete(d.did_id));
        return n;
      }
      const n = new Set(s);
      rows.forEach((d) => n.add(d.did_id));
      return n;
    });
  };
  useEffect(() => { setSelectedIds(new Set()); setSelectAllMatching(false); }, [debouncedSearch, appFilter, page]);

  // Iter 87: build the same filter params list_dids would receive, so
  // "all_matching" mode on the backend uses the exact query the user is
  // looking at.
  const currentFilterParams = () => {
    const params = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (appFilter !== "all") params.application = appFilter;
    Object.entries(colFilters).forEach(([k, v]) => {
      if ((v || "").trim()) params[filterFieldFor(k)] = v;
    });
    return params;
  };

  const runBulkAssign = async () => {
    if (!bulkOrgId) { toast.error("Pick a partner"); return; }
    const orgId = bulkOrgId === "__none__" ? "" : bulkOrgId;
    const t = toast.loading("Reassigning…");
    try {
      setBulkBusy(true);
      const body = selectAllMatching
        ? { all_matching: true, filters: currentFilterParams(), partner_org_id: orgId }
        : { did_ids: Array.from(selectedIds),                    partner_org_id: orgId };
      if (!selectAllMatching && !selectedIds.size) {
        toast.error("Nothing selected", { id: t }); return;
      }
      const { data } = await api.post("/dids/bulk-assign-partner", body);
      toast.success(`Reassigned ${data.modified.toLocaleString()} DID${data.modified === 1 ? "" : "s"}`, { id: t });
      setSelectedIds(new Set());
      setSelectAllMatching(false);
      setBulkOrgId("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk assign failed", { id: t });
    } finally { setBulkBusy(false); }
  };

  // Iter 88: RealValidito DNC + line-type lookup on the current selection
  // (or all-matching filter). Cost guardrail: if the backend reports
  // requires_confirmation, we pop a confirm() with the projected cost
  // before re-submitting with confirm_cost:true.
  const [lookupBusy, setLookupBusy] = useState(false);
  const runLookup = async ({ confirm_cost = false } = {}) => {
    let numbers = [];
    if (selectAllMatching) {
      // Fetch every matching number for the current filter set.
      const params = currentFilterParams();
      const { data } = await api.get("/dids", { params: { ...params, limit: 60000 } });
      numbers = (data.items || []).map((d) => d.number).filter(Boolean);
    } else {
      numbers = rows.filter((r) => selectedIds.has(r.did_id)).map((r) => r.number).filter(Boolean);
    }
    if (!numbers.length) { toast.error("No numbers to look up"); return; }
    const t = toast.loading(`Looking up ${numbers.length.toLocaleString()} number${numbers.length === 1 ? "" : "s"}…`);
    try {
      setLookupBusy(true);
      const { data } = await api.post("/phone-lookups/lookup",
        { numbers, confirm_cost, include_phone: true, include_dnc: true });
      if (data.requires_confirmation) {
        toast.dismiss(t);
        const ok = window.confirm(
          `${data.message}\n\n(${data.cached.toLocaleString()} already cached will be free.)`
        );
        if (ok) return runLookup({ confirm_cost: true });
        return;
      }
      const clean  = data.items.filter((x) => x.dnc_status === "clean").length;
      const tcpa   = data.items.filter((x) => x.dnc_status === "tcpa_litigator").length;
      const dnc    = data.items.filter((x) => x.dnc_status === "federal_dnc").length;
      const invalid= data.items.filter((x) => x.dnc_status === "invalid" || !x.is_valid).length;
      toast.success(
        `Looked up ${data.items.length.toLocaleString()} · ${clean} clean · ${tcpa} TCPA · ${dnc} federal DNC · ${invalid} invalid` +
        (data.sandbox ? " (sandbox)" : ""),
        { id: t, duration: 6500 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lookup failed", { id: t });
    } finally { setLookupBusy(false); }
  };
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Server fetch driver — hits /api/dids with paging + filter params
  // whenever any of them changes.
  const load = () => {
    setLoading(true);
    const params = {
      offset:  page * pageSize,
      limit:   pageSize,
      sort_key: sort.key,
      sort_dir: sort.dir,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (appFilter !== "all") params.application = appFilter;
    Object.entries(colFilters).forEach(([k, v]) => {
      if ((v || "").trim()) params[filterFieldFor(k)] = v;
    });
    api.get("/dids", { params })
      .then(async (r) => {
        const items = r.data.items || [];
        setRows(items);
        setTotal(r.data.total || 0);
        // Iter 88: merge cached phone-lookup data onto each row so the
        // Number column can show DNC + line-type pills. Cache-only; never
        // triggers a fresh RealValidito call.
        try {
          const numbers = items.map((d) => d.number).filter(Boolean);
          if (numbers.length) {
            const { data: lu } = await api.post("/phone-lookups/batch-cached", { numbers });
            const byN = lu.items || {};
            const norm = (s) => (String(s || "").replace(/\D/g, "").slice(-10));
            setRows(items.map((d) => ({ ...d, _lookup: byN[norm(d.number)] || null })));
          }
        } catch { /* pill display is best-effort */ }
      })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [page, pageSize, sort.key, sort.dir, debouncedSearch, appFilter, colFilters]);
  useEffect(() => { setPage(0); }, [debouncedSearch, appFilter, pageSize, sort.key, sort.dir, colFilters]);
  useEffect(() => {
    api.get("/did-applications").then((r) => {
      setApps(r.data.applications || []);
      setSbcOptions(r.data.sbc_options || {});
      setPartners(r.data.partners || []);
      setCarrierStatuses(r.data.carrier_statuses || []);
    }).catch(() => {});
  }, []);

  // Iter 83: pick up ?partner=<org_id> from Partners drill-down and
  // pre-filter to just that partner's DIDs (search by name).
  const [partnerChip, setPartnerChip] = useState(null); // {org_id, name}
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const partnerId = params.get("partner");
    if (!partnerId) return;
    api.get("/organizations", { params: { type: "partner" } })
      .then((r) => {
        const hit = (r.data || []).find((o) => o.org_id === partnerId);
        if (hit?.name) {
          setSearch(hit.name);
          setPartnerChip({ org_id: hit.org_id, name: hit.name });
        }
      })
      .catch(() => {});
  }, []);

  // Clear the chip if the user edits the search box away from the partner name.
  useEffect(() => {
    if (partnerChip && search !== partnerChip.name) {
      setPartnerChip(null);
    }
  }, [search, partnerChip]);

  // Iter 83b: server handles filter + sort. `filtered` == `rows`
  // (current page). Kept the name so downstream refs still work.
  const filtered = rows;

  // filter row uses live colOptions state above (server-fetched on open)
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const onDrop = (target) => {
    if (!dragKey || dragKey === target) return;
    setOrder((ord) => {
      const next = ord.filter((k) => k !== dragKey);
      const idx = next.indexOf(target);
      next.splice(idx, 0, dragKey);
      persist(next, hidden);
      return next;
    });
    setDragKey(null);
  };

  const save = async () => {
    const f = dialog.form;
    if (!f.number.trim()) { toast.error("Number is required"); return; }
    const payload = { ...f, cost: parseFloat(f.cost) || 0, first_enabled: f.first_enabled || null };
    try {
      if (dialog.mode === "add") await api.post("/dids", payload);
      else await api.put(`/dids/${f.did_id}`, payload);
      toast.success(dialog.mode === "add" ? "DID added" : "DID updated");
      setDialog(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/dids/${delTarget.did_id}`); toast.success("DID deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setField = (k, v) => setDialog((d) => ({ ...d, form: { ...d.form, [k]: v } }));

  // Robust download (handles sandboxed preview iframe via shared helper).

  const exportPdf = async () => {
    const t = toast.loading("Fetching all rows for PDF…");
    try {
      const { data } = await api.get("/dids", { params: {
        limit: 60000, offset: 0, sort_key: sort.key, sort_dir: sort.dir,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(appFilter !== "all" ? { application: appFilter } : {}),
      } });
      const all = data.items || [];
      const cols = visibleCols;
      const filterDesc = [appFilter !== "all" ? `Application: ${appFilter}` : null, debouncedSearch ? `Search: "${debouncedSearch}"` : null].filter(Boolean).join(" · ");
      const body = {
        title: "DID Master",
        subtitle: `${all.length} record(s)${filterDesc ? " · " + filterDesc : ""} · sorted by ${COL_MAP[sort.key]?.label || sort.key} (${sort.dir})`,
        headers: cols.map((c) => c.label),
        aligns: cols.map((c) => c.align || "left"),
        rows: all.map((d) => cols.map((c) => c.text(d))),
      };
      const res = await api.post("/dids/pdf-doc", body);
      await downloadFromUrl(`/files/${res.data.token}`, res.data.filename);
      toast.success("PDF downloaded", { id: t });
    } catch { toast.error("Failed to export PDF", { id: t }); }
  };

  const exportCsv = async () => {
    const t = toast.loading("Fetching all rows for CSV…");
    try {
      const { data } = await api.get("/dids", { params: {
        limit: 60000, offset: 0, sort_key: sort.key, sort_dir: sort.dir,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(appFilter !== "all" ? { application: appFilter } : {}),
      } });
      const all = data.items || [];
      const cols = visibleCols;
      const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
      const lines = [cols.map((c) => esc(c.label)).join(",")];
      all.forEach((d) => lines.push(cols.map((c) => esc(c.text(d))).join(",")));
      downloadText("\uFEFF" + lines.join("\n"), `DID-Master-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success(`CSV exported (${all.length.toLocaleString()} rows)`, { id: t });
    } catch { toast.error("Failed to export CSV", { id: t }); }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-1" data-testid="did-master-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">DID Master</h1>
          <p className="mt-1 text-sm text-slate-500">Master number inventory — applications, SMS, carriers &amp; cost.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openHistory} data-testid="did-upload-history-btn">
            <History className="h-4 w-4" /> Upload history
          </Button>
          {isSuperAdmin && (
            <>
              <Button variant="outline" asChild
                      data-testid="did-territory-aliases-btn"
                      className="border-slate-300">
                <a href="/territory-aliases">
                  <LinkIcon className="h-4 w-4" /> Territory aliases
                </a>
              </Button>
              <input ref={csvUploadRef} type="file" accept=".csv,text/csv"
                     onChange={onCsvChosen} className="hidden"
                     data-testid="did-csv-upload-input" />
              <Button variant="outline" onClick={openCsvUpload}
                      disabled={uploadBusy}
                      data-testid="did-upload-csv-btn"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50">
                <Upload className="h-4 w-4" /> {uploadBusy ? "Importing…" : "Upload CSV"}
              </Button>
            </>
          )}
          {canCreate && (
            <Button variant="outline" data-testid="did-ai-command-button" onClick={() => setAiOpen(true)} className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/40">
              <Sparkles className="h-4 w-4" /> AI command
            </Button>
          )}
          {canCreate && (
            <Button variant="outline" data-testid="did-bulk-button" onClick={() => setBulkOpen(true)}>
              <Layers className="h-4 w-4" /> Bulk add
            </Button>
          )}
          {canCreate && (
            <Button data-testid="did-add-button" onClick={() => setDialog({ mode: "add", form: { ...EMPTY, application: apps[0] || "UCaaS" } })}>
              <Plus className="h-4 w-4" /> Add DID
            </Button>
          )}
        </div>
      </div>

      {/* Iter 88o — Live DID Feed status pill. Reads /api/did-feed/status
          and, for super admins with a non-off provider, shows a Sync-now
          button that fires /api/did-feed/sync. */}
      <DidFeedStatus isSuperAdmin={isSuperAdmin} onAfterSync={() => load()} />

      <div className="flex flex-wrap items-center gap-3">
        <PageSizeSelect pageSize={pageSize} setPageSize={setPageSize} testid="did-page-size" />
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search digits or vanity letters (e.g. 1-800-FLOWERS), treatment, carrier…" className="pl-8 pr-32" data-testid="did-search" />
          {partnerChip && (
            <span className="absolute right-2 top-1.5 inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-800"
                  data-testid="did-partner-chip">
              <Phone className="h-3 w-3" /> {partnerChip.name}
              <button type="button"
                      onClick={() => { setPartnerChip(null); setSearch(""); }}
                      className="ml-0.5 rounded-full hover:bg-sky-200"
                      aria-label="Clear partner filter"
                      data-testid="did-partner-chip-clear">
                <XIcon className="h-2.5 w-2.5" />
              </button>
            </span>
          )}
          {!partnerChip && /[a-z]/i.test(search) && vanityToDigits(search) && (
            <Badge variant="outline" className="absolute right-2 top-1.5 bg-slate-100 text-slate-600 border-slate-200 font-mono text-[11px]" data-testid="did-vanity-hint">matching {vanityToDigits(search)}</Badge>
          )}
        </div>
        <Select value={appFilter} onValueChange={setAppFilter}>
          <SelectTrigger className="w-[170px]" data-testid="did-app-filter"><SelectValue placeholder="Application" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All applications</SelectItem>
            {apps.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Iter 90j — shared toolbar (matches Partners): result count · Columns · CSV · PDF. */}
        <ColumnsToolbar
          testidPrefix="did"
          count={rows.length}
          columns={toolbarColumns}
          visibleIds={toolbarVisibleIds}
          onToggle={toggleHidden}
          onMove={moveCol}
          onReset={resetCols}
          onExportCsv={exportCsv}
          onExportPdf={exportPdf}
          extraLeft={<span className="text-[11px] text-slate-400 mr-1" data-testid="did-count-secondary">of {total.toLocaleString()}</span>}
        />
      </div>

      {/* Iter 84 + 87: bulk action bar — shows when at least one row is
          selected. When every row on the current page is selected AND
          more matches exist beyond the page, a sky pill offers to expand
          the selection across the entire server-side filter. */}
      {(selectedIds.size > 0 || selectAllMatching) && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900"
             data-testid="did-bulk-bar">
          <span className="font-medium">
            {selectAllMatching
              ? `All ${total.toLocaleString()} matching selected`
              : `${selectedIds.size} selected`}
          </span>
          {!selectAllMatching && allSelected && total > rows.length && (
            <button type="button"
                    onClick={() => setSelectAllMatching(true)}
                    data-testid="did-select-all-matching"
                    className="rounded-full border border-sky-300 bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700 hover:bg-sky-200">
              Select all {total.toLocaleString()} matching current filter
            </button>
          )}
          {selectAllMatching && (
            <button type="button"
                    onClick={() => setSelectAllMatching(false)}
                    data-testid="did-clear-all-matching"
                    className="rounded-full border border-indigo-300 bg-white px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100">
              Clear all-matching
            </button>
          )}
          <span className="text-indigo-500">·</span>
          <span>Reassign to:</span>
          <Select value={bulkOrgId} onValueChange={setBulkOrgId}>
            <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="did-bulk-org-picker">
              <SelectValue placeholder="— pick a partner —" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__none__">(clear partner)</SelectItem>
              {partners.map((p) => (
                <SelectItem key={p.org_id} value={p.org_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={runBulkAssign} disabled={!bulkOrgId || bulkBusy}
                  data-testid="did-bulk-apply">
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
            Apply
          </Button>
          <Button size="sm" variant="ghost"
                  onClick={() => { setSelectedIds(new Set()); setSelectAllMatching(false); }}
                  data-testid="did-bulk-clear"
                  className="text-indigo-600 hover:bg-indigo-100">Clear selection</Button>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="max-h-[68vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={selectAllMatching || allSelected}
                         onChange={toggleSelectAll}
                         data-testid="did-select-all"
                         aria-label="Select all rows on this page" />
                </th>
                {visibleCols.map((c) => (
                  <th key={c.key} draggable onDragStart={() => setDragKey(c.key)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(c.key)}
                    className={`select-none px-3 py-2 font-semibold text-slate-500 ${c.align === "right" ? "text-right" : "text-left"} ${dragKey === c.key ? "opacity-40" : ""}`}
                    data-testid={`did-th-${c.key}`}>
                    <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                      <GripVertical className="h-3.5 w-3.5 cursor-grab text-slate-300" />
                      <span className={c.sortable ? "cursor-pointer hover:text-slate-700" : ""} onClick={() => c.sortable && toggleSort(c.key)} data-testid={c.sortable ? `did-sort-${c.key}` : undefined}>
                        {c.label}{c.sortable && sort.key === c.key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                      </span>
                      {c.sortable && sort.key !== c.key && <ArrowUpDown className="h-3 w-3 opacity-30" />}
                    </span>
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-semibold text-slate-500">Actions</th>
              </tr>
              <tr className="border-b border-slate-200 bg-white" data-testid="did-filter-row">
                <th className="px-2 py-1.5"></th>
                {visibleCols.map((c) => (
                  <th key={c.key} className="px-2 py-1.5 font-normal">
                    {FILTERABLE_KEYS.has(c.key) ? (
                      <Select value={colFilters[c.key] || "__all__"}
                              onValueChange={(v) => setColFilters((f) => {
                                const nf = { ...f };
                                if (v === "__all__") delete nf[c.key]; else nf[c.key] = v;
                                return nf;
                              })}
                              onOpenChange={(open) => open && loadColOptions(c.key)}>
                        <SelectTrigger className="h-7 text-xs font-normal"
                                       data-testid={`did-colfilter-${c.key}`}>
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="__all__">All</SelectItem>
                          {(colOptions[c.key] || []).map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : <span className="text-[10px] text-slate-300">—</span>}
                  </th>
                ))}
                <th className="px-2 py-1.5 text-right">
                  {Object.keys(colFilters).length > 0 && (
                    <button type="button" onClick={() => setColFilters({})}
                            className="text-[11px] font-medium text-rose-500 hover:underline"
                            data-testid="did-clear-filters">Clear</button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={visibleCols.length + 2} className="py-10 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={visibleCols.length + 2} className="py-10 text-center text-slate-400">No DIDs found.</td></tr>
              ) : filtered.map((d) => (
                <tr key={d.did_id}
                    className={`border-b border-slate-100 hover:bg-slate-50/60 ${(selectAllMatching || selectedIds.has(d.did_id)) ? "bg-indigo-50/40" : ""}`}
                    data-testid={`did-row-${d.did_id}`}>
                  <td className="px-3 py-2">
                    <input type="checkbox"
                           checked={selectAllMatching || selectedIds.has(d.did_id)}
                           disabled={selectAllMatching}
                           onChange={() => toggleSelect(d.did_id)}
                           data-testid={`did-select-${d.did_id}`}
                           aria-label={`Select DID ${d.number}`} />
                  </td>
                  {visibleCols.map((c) => (
                    <td key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right" : ""}`}>{c.render(d)}</td>
                  ))}
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setLogTarget(d)} data-testid={`did-log-${d.did_id}`} title="Change log"><History className="h-4 w-4 text-slate-500" /></Button>
                      {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDialog({ mode: "edit", form: { ...d } })} data-testid={`did-edit-${d.did_id}`} title="Edit"><Pencil className="h-4 w-4 text-slate-500" /></Button>}
                      {canDelete && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDelTarget(d)} data-testid={`did-delete-${d.did_id}`} title="Delete"><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-xs text-slate-500"
             data-testid="did-pagination">
          <span>
            {total === 0 ? "0" : `${(page * pageSize + 1).toLocaleString()}–${Math.min(total, (page + 1) * pageSize).toLocaleString()}`} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    data-testid="did-prev-page">
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <span>Page {page + 1} of {Math.max(1, Math.ceil(total / pageSize))}</span>
            <Button variant="outline" size="sm" disabled={(page + 1) * pageSize >= total}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="did-next-page">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={dialog !== null} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="did-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "add" ? "Add DID" : "Edit DID"}</DialogTitle>
            <DialogDescription>Manage this number's application, routing, carrier and 911 details.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5"><Label>Number *</Label><Input value={dialog.form.number} onChange={(e) => setField("number", e.target.value)} placeholder="+1 403 555 1234" data-testid="did-field-number" /></div>
              <div className="space-y-1.5"><Label>Application</Label>
                <Select value={dialog.form.application || ""} onValueChange={(v) => { const opts = sbcOptions[v] || []; setDialog((d) => ({ ...d, form: { ...d.form, application: v, sbc: opts.includes(d.form.sbc) ? d.form.sbc : (opts[0] || "") } })); }}>
                  <SelectTrigger data-testid="did-field-application"><SelectValue /></SelectTrigger>
                  <SelectContent>{apps.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>SBC (Geo assignment)</Label>
                <Select value={dialog.form.sbc || ""} onValueChange={(v) => setField("sbc", v)}>
                  <SelectTrigger data-testid="did-field-sbc"><SelectValue placeholder="Select SBC" /></SelectTrigger>
                  <SelectContent>{(sbcOptions[dialog.form.application] || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Carrier</Label><Input value={dialog.form.carrier || ""} onChange={(e) => setField("carrier", e.target.value)} data-testid="did-field-carrier" /></div>
              <div className="space-y-1.5"><Label>Carrier status</Label>
                <Select value={dialog.form.carrier_status || ""} onValueChange={(v) => setField("carrier_status", v)}>
                  <SelectTrigger data-testid="did-field-carrier-status"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>{carrierStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Rate center</Label><Input value={dialog.form.rate_center || ""} onChange={(e) => setField("rate_center", e.target.value)} placeholder="CALGARY" data-testid="did-field-rate-center" /></div>
              <div className="space-y-1.5"><Label>Province/State</Label><Input value={dialog.form.rate_center_state || ""} onChange={(e) => setField("rate_center_state", e.target.value)} placeholder="AB" data-testid="did-field-rate-center-state" /></div>
              <div className="space-y-1.5"><Label>Partner</Label>
                <Select value={dialog.form.partner_org_id || ""} onValueChange={(v) => setField("partner_org_id", v)}>
                  <SelectTrigger data-testid="did-field-partner"><SelectValue placeholder="Select partner" /></SelectTrigger>
                  <SelectContent>{partners.map((p) => <SelectItem key={p.org_id} value={p.org_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Domain</Label><Input value={dialog.form.domain || ""} onChange={(e) => setField("domain", e.target.value)} placeholder="northwind.uc.ca" data-testid="did-field-domain" /></div>
              <div className="space-y-1.5">
                <Label>Location / extension</Label>
                <Input value={dialog.form.location || ""}
                       onChange={(e) => setField("location", e.target.value)}
                       placeholder="e.g. 202"
                       data-testid="did-field-location" />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div className="text-sm font-medium text-slate-700">SMS enabled</div>
                <Switch checked={!!dialog.form.sms_enabled} onCheckedChange={(v) => setField("sms_enabled", v)} data-testid="did-field-sms" />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div className="text-sm font-medium text-slate-700">911 Registered</div>
                <Switch checked={!!dialog.form.e911_registered} onCheckedChange={(v) => setField("e911_registered", v)} data-testid="did-field-e911" />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div className="text-sm font-medium text-slate-700">Billing</div>
                <Switch checked={!!dialog.form.billing} onCheckedChange={(v) => setField("billing", v)} data-testid="did-field-billing" />
              </div>
              <div className="space-y-1.5"><Label>First enabled</Label><Input type="date" value={(dialog.form.first_enabled || "").slice(0, 10)} onChange={(e) => setField("first_enabled", e.target.value)} data-testid="did-field-first-enabled" /></div>
              <div className="col-span-2 space-y-1.5"><Label>Treatment</Label><Input value={dialog.form.treatment || ""} onChange={(e) => setField("treatment", e.target.value)} placeholder="Route to UCaaS / Hunt group / IVR…" data-testid="did-field-treatment" /></div>
              <div className="space-y-1.5"><Label>Cost ($/mo)</Label><Input type="number" step="0.01" value={dialog.form.cost} onChange={(e) => setField("cost", e.target.value)} data-testid="did-field-cost" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} data-testid="did-save-button">{dialog?.mode === "add" ? "Add DID" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change log dialog */}
      <Dialog open={logTarget !== null} onOpenChange={(o) => !o && setLogTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="did-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Change log — {logTarget?.number}</DialogTitle>
            <DialogDescription>Most recent first. Records who changed what and when.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-auto">
            {[...(logTarget?.change_log || [])].reverse().map((e, i) => (
              <div key={i} className="rounded-lg border border-slate-100 p-3" data-testid={`did-log-entry-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">{e.action}</span>
                  <span className="text-xs text-slate-400">{fmtDateTime(e.at)}</span>
                </div>
                <p className="text-xs text-slate-500">by {e.by_name || "System"}</p>
                {(e.changes || []).length > 0 && (
                  <ul className="mt-1.5 space-y-0.5">
                    {e.changes.map((c, j) => (
                      <li key={j} className="text-xs text-slate-600"><span className="font-medium">{c.field}:</span> <span className="text-slate-400 line-through">{String(c.old)}</span> → <span className="text-slate-800">{String(c.new)}</span></li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {!(logTarget?.change_log || []).length && <p className="py-6 text-center text-sm text-slate-400">No history.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={delTarget !== null} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="did-delete-dialog">
          <DialogHeader><DialogTitle>Delete DID</DialogTitle><DialogDescription>Remove {delTarget?.number}? This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="did-confirm-delete">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AiDidCommand open={aiOpen} onOpenChange={setAiOpen} onDone={load} />
      <BulkAddDid open={bulkOpen} onOpenChange={setBulkOpen} apps={apps} carrierStatuses={carrierStatuses} sbcOptions={sbcOptions} onDone={load} />

      {/* Iter 83: Import diff preview dialog — shown after Upload CSV */}
      <Dialog open={preview !== null} onOpenChange={(o) => { if (!o) cancelImport(); }}>
        <DialogContent className="max-w-3xl" data-testid="did-import-preview">
          <DialogHeader>
            <DialogTitle>Review import: {preview?.source_file}</DialogTitle>
            <DialogDescription>
              {preview?.row_count?.toLocaleString()} rows in CSV · {preview?.linked?.toLocaleString()} auto-linked to a partner.
              Nothing is written until you Apply.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5" data-testid="did-import-counts">
                <PreviewStat label="Added"     value={preview.diff.counts.added}     tone="emerald" />
                <PreviewStat label="Updated"   value={preview.diff.counts.updated}   tone="sky" />
                <PreviewStat label="Conflicts" value={preview.diff.counts.conflicts} tone="amber" />
                <PreviewStat label="Unchanged" value={preview.diff.counts.unchanged} tone="slate" />
                <PreviewStat label="Removed"   value={preview.diff.counts.removed}   tone="rose" />
              </div>

              {preview.diff.counts.removed > 0 && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800"
                     data-testid="did-import-removed-block">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input type="checkbox" checked={deleteRemoved}
                           onChange={(e) => setDeleteRemoved(e.target.checked)}
                           data-testid="did-import-delete-removed" />
                    <span>
                      Delete <strong>{preview.diff.counts.removed}</strong> DIDs that are in the master but not in this CSV.
                      Off by default — keeps existing rows.
                    </span>
                  </label>
                  {deleteRemoved && (
                    <div className="mt-2 max-h-24 overflow-auto rounded border border-rose-200 bg-white p-2 font-mono text-[10px] text-rose-700">
                      {preview.diff.removed.slice(0, 20).map((r) => (
                        <div key={r.did_id}>+{r.number} — {r.partner_name || "—"}</div>
                      ))}
                      {preview.diff.counts.removed > 20 &&
                        <div className="text-rose-500">…and {preview.diff.counts.removed - 20} more</div>}
                    </div>
                  )}
                </div>
              )}

              {preview.diff.counts.conflicts > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                     data-testid="did-import-conflicts-block">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium">
                      {preview.diff.counts.conflicts} conflict{preview.diff.counts.conflicts === 1 ? "" : "s"}
                      — these DIDs were edited by staff since the last import.
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline"
                              onClick={() => setConflictPicks(Object.fromEntries(preview.diff.conflicts.map((c) => [c.did_id, "csv"])))}
                              data-testid="did-import-accept-all-csv">
                        CSV wins all
                      </Button>
                      <Button size="sm" variant="outline"
                              onClick={() => setConflictPicks(Object.fromEntries(preview.diff.conflicts.map((c) => [c.did_id, "db"])))}
                              data-testid="did-import-keep-all-db">
                        DB wins all
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 max-h-56 space-y-1.5 overflow-auto pr-1">
                    {preview.diff.conflicts.slice(0, 100).map((c) => {
                      const pick = conflictPicks[c.did_id] || "db";
                      return (
                        <div key={c.did_id}
                             data-testid={`did-conflict-row-${c.did_id}`}
                             className="rounded border border-amber-200 bg-white p-2">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-[11px] font-semibold text-slate-800">+{c.number}</span>
                            <span className="text-[10px] text-slate-500">{c.partner_name || "—"}</span>
                            <div className="flex gap-3 text-[11px]">
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name={`c-${c.did_id}`}
                                       checked={pick === "db"}
                                       onChange={() => setConflictPicks((p) => ({ ...p, [c.did_id]: "db" }))}
                                       data-testid={`conflict-pick-db-${c.did_id}`} />
                                Keep DB
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name={`c-${c.did_id}`}
                                       checked={pick === "csv"}
                                       onChange={() => setConflictPicks((p) => ({ ...p, [c.did_id]: "csv" }))}
                                       data-testid={`conflict-pick-csv-${c.did_id}`} />
                                Use CSV
                              </label>
                            </div>
                          </div>
                          <div className="mt-1 space-y-0.5 text-[10px] text-slate-600">
                            {c.changes.slice(0, 4).map((ch, i) => (
                              <div key={i}>
                                <span className="font-medium">{ch.field}:</span>{" "}
                                <span className="text-slate-400 line-through">{String(ch.db || "—")}</span> →{" "}
                                <span className="text-slate-800">{String(ch.csv || "—")}</span>
                              </div>
                            ))}
                            {c.changes.length > 4 && <div className="text-slate-400">…and {c.changes.length - 4} more fields</div>}
                          </div>
                        </div>
                      );
                    })}
                    {preview.diff.counts.conflicts > 100 &&
                      <div className="text-center text-slate-500">
                        Showing first 100 of {preview.diff.counts.conflicts} — the rest default to DB unless you use the bulk actions above.
                      </div>}
                  </div>
                </div>
              )}

              {preview.diff.counts.added === 0 &&
               preview.diff.counts.updated === 0 &&
               preview.diff.counts.conflicts === 0 &&
               preview.diff.counts.removed === 0 && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-600">
                  Nothing to apply — CSV matches the current master exactly.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={cancelImport} disabled={uploadBusy}
                    data-testid="did-import-cancel">Cancel</Button>
            <Button onClick={() => applyImport()} disabled={uploadBusy}
                    data-testid="did-import-apply">
              {uploadBusy ? "Applying…" : "Apply import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 82: Upload history dialog — rolling log of DID CSV imports */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl" data-testid="did-upload-history-dialog">
          <DialogHeader>
            <DialogTitle>DID CSV upload history</DialogTitle>
            <DialogDescription>
              Every CSV import of the DID master, newest first.
              A live multi-source feed is on the roadmap.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-auto">
            {historyItems.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">No uploads recorded yet.</p>
            ) : historyItems.map((h, i) => (
              <div key={h.history_id || i}
                   data-testid={`did-history-entry-${i}`}
                   className="rounded-lg border border-slate-100 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-800">
                      {h.source_file || "upload.csv"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {(h.uploaded_by?.name || h.uploaded_by?.email || "Unknown")} · {fmtDateTime(h.uploaded_at)}
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="font-mono font-semibold text-slate-700">
                      {(h.row_count || 0).toLocaleString()} rows
                    </div>
                    {h.notes && <div className="text-slate-500">{h.notes}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewStat({ label, value, tone }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    sky:     "border-sky-200 bg-sky-50 text-sky-800",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
    slate:   "border-slate-200 bg-slate-50 text-slate-700",
    rose:    "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`rounded-md border p-2 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wide">{label}</div>
      <div className="mt-0.5 font-heading text-lg font-bold">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

